import { Injectable } from '@angular/core';
// TODO: https://git.sys.cigna.com/angular-guild/cigna-nx/issues/567
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  FeatureTogglesFacade,
  FeatureToggleType,
  updateFeatureToggles,
} from '@cigna/features';
import { OmniConversationsFacade } from '@cigna/omni/conversations-state';
import { OmnibusDataAccessService } from '@cigna/omni/data-access';
import { OmniInterceptionsFacade } from '@cigna/omni/interceptions-state';
import {
  getUTCOffset,
  OmniLiveAvailabilityFacade,
} from '@cigna/omni/live-availability-state';
import { OmniPollingFacade } from '@cigna/omni/polling-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  OmnibusInitializationActionTypes,
  OmnibusInitializeFailure,
  OmnibusInitializeSuccess,
} from './omnibus-initialization.actions';
import {
  OmnibusResultProperties,
  OmnibusResultTypes,
} from './omnibus-initialization.interfaces';

@Injectable()
export class OmnibusInitializationEffects {
  omnibusInitializeNoop$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OmnibusInitializationActionTypes.OmnibusInitializeNoop),
        switchMap(() => this.service.postOmnibus([])),
      ),
    { dispatch: false },
  );

  omnibusInitialize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFeatureToggles),
      switchMap(() =>
        this.featureTogglesFacade.featuresEnabled([
          'isUserAuthorized',
          'isNotImpersonator',
        ]),
      ),
      withLatestFrom(
        this.featureTogglesFacade.featuresEnabled(['hasRestrictedChat']),
        this.featureTogglesFacade.featuresEnabled([
          'shouldEnableChat', // to disable or enable restricted chat at release level
          'canCallOmnibusApi', // to make omnibus call based on page
        ]),
      ),
      filter(
        ([chatEnabled, restrictedChat, enableRestrictedChat]) =>
          chatEnabled && (!restrictedChat || enableRestrictedChat),
      ),
      take(1),
      switchMap(() =>
        this.service
          .postOmnibus([
            {
              seq: 0,
              // eslint-disable-next-line sonarjs/no-duplicate-string
              type: 'v2/properties',
              payload: null,
            },
            {
              seq: 1,
              type: 'v1/conversations',
              payload: { types: ['async', 'live'] },
            },
            {
              seq: 2,
              type: 'v1/availability/live',
              payload: {
                offset: getUTCOffset(new Date().getTimezoneOffset()),
                format: '12h',
              },
            },
            // {
            //   seq: 3,
            //   type: 'v1/interceptions',
            //   payload: null,
            // },
          ])
          .pipe(
            map(
              (omnibusResults) => new OmnibusInitializeSuccess(omnibusResults),
            ),
            catchError((error) => of(new OmnibusInitializeFailure(error))),
          ),
      ),
    ),
  );

  omnibusInitializeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OmnibusInitializeSuccess>(
          OmnibusInitializationActionTypes.OmnibusInitializeSuccess,
        ),
        map((res) => {
          const properties = res.payload.find(
            (data) => data.type === 'v2/properties',
          );
          const generalChat = (properties as OmnibusResultProperties)?.success
            ?.features?.generalChat;
          return generalChat
            ? res
            : { ...res, payload: properties ? [properties] : [] };
        }),
        withLatestFrom(
          this.featureTogglesFacade.featuresEnabled(['shouldEnableChat']),
        ),
        // eslint-disable-next-line sonarjs/cognitive-complexity
        tap(([{ payload }, enableNewChat]) => {
          // eslint-disable-next-line sonarjs/cognitive-complexity
          payload.forEach((result) => {
            switch (result?.type) {
              case 'v2/properties':
                if (result.success) {
                  const features = result.success.features;
                  const featuresFlags: Record<string, boolean> = {};

                  Object.keys(features).forEach((k) => {
                    const value = features[k];
                    if (typeof value === 'boolean') {
                      featuresFlags[k] = value;
                    }
                  });

                  this.featureTogglesFacade.updateFeatureToggles(
                    featuresFlags,
                    FeatureToggleType.OPS,
                  );

                  if (
                    typeof result.success.features.activityDebounce === 'number'
                  ) {
                    this.conversationsFacade.setActivityDebounceTime(
                      result.success.features.activityDebounce,
                    );
                  }
                  if (
                    typeof result.success.features.activeChatProvider ===
                    'string'
                  ) {
                    this.conversationsFacade.setActiveChatProvider(
                      result.success.features.activeChatProvider,
                    );
                  }
                  this.pollingFacade.pollingConfigLoaded(
                    result.success.pollingWeb,
                  );
                } else {
                  this.pollingFacade.pollingConfigError(result.failure);
                }
                break;

              case 'v1/availability/live':
                if (result.success) {
                  this.liveAvailabilityFacade.setLiveAvailability(
                    result.success,
                  );
                }
                break;

              case 'v1/conversations':
                // eslint-disable-next-line no-unused-expressions
                !enableNewChat
                  ? result.success
                    ? this.conversationsFacade.loadConversationsSuccess(
                        result.success,
                      )
                    : this.conversationsFacade.loadConversationsFailure(
                        result.failure,
                      )
                  : this.conversationsFacade.getActiveConversation();
                break;

              // case 'v1/interceptions':
              //   if (result.success) {
              //     this.interceptionsFacade.addMany(result.success);
              //   }
              //   break;

              default:
                break;
            }
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private service: OmnibusDataAccessService,
    private conversationsFacade: OmniConversationsFacade,
    private interceptionsFacade: OmniInterceptionsFacade,
    private liveAvailabilityFacade: OmniLiveAvailabilityFacade,
    private pollingFacade: OmniPollingFacade,
    private featureTogglesFacade: FeatureTogglesFacade,
  ) {}
}
